import { DATA, ACTIONS } from '@wix/component-protocol/schema';
function getInnerElementsChildren(element) {
  var _element$inlineElemen, _element$refElement;
  return ((_element$inlineElemen = element.inlineElement) == null ? void 0 : _element$inlineElemen.elements) || ((_element$refElement = element.refElement) == null || (_element$refElement = _element$refElement.resolvedElement) == null ? void 0 : _element$refElement.elements);
}
function checkElementActionable(manifest, actionName) {
  if (!manifest) {
    return false;
  }
  switch (actionName) {
    case ACTIONS.ACTION_NAME.design:
      {
        const hasCssProperties = !!(manifest.cssProperties && Object.keys(manifest.cssProperties).length > 0);
        const hasCssCustomProperties = !!(manifest.cssCustomProperties && Object.keys(manifest.cssCustomProperties).length > 0);
        return hasCssProperties || hasCssCustomProperties;
      }
    case ACTIONS.ACTION_NAME.settings:
      {
        if (!manifest.data) {
          return false;
        }
        const filteredData = Object.entries(manifest.data).filter(_ref => {
          let [_key, dataItem] = _ref;
          return (dataItem == null ? void 0 : dataItem.dataType) !== DATA.DATA_TYPE.arrayItems;
        });
        return filteredData.length > 0;
      }
    case ACTIONS.ACTION_NAME.manageItems:
      {
        if (!manifest.data) {
          return false;
        }
        const filteredData = Object.entries(manifest.data).filter(_ref2 => {
          let [_key, dataItem] = _ref2;
          return (dataItem == null ? void 0 : dataItem.dataType) === DATA.DATA_TYPE.arrayItems;
        });
        return filteredData.length > 0;
      }
    default:
      return false;
  }
}
export class ManifestStructureHelpers {
  constructor(editorPointerAPI) {
    this.editorPointerAPI = editorPointerAPI;
  }
  getAncestors(editorPointer) {
    const documentPointer = this.editorPointerAPI.getDocumentPointer(editorPointer);
    const innerElementPath = this.editorPointerAPI.getInnerElementPath(editorPointer);
    if (!innerElementPath || innerElementPath.length === 1) {
      return [];
    }
    const initialPointer = this.editorPointerAPI.buildEditorPointer(documentPointer, innerElementPath.slice(0, 1));
    const initialPointerInnerElementPath = this.editorPointerAPI.getInnerElementPath(initialPointer);
    const path = innerElementPath.slice(1, innerElementPath.length - 1);
    return path.reduce((acc, key) => {
      const lastPointer = acc[acc.length - 1];
      const nextDocumentPointer = this.editorPointerAPI.getDocumentPointer(lastPointer);
      const lastPointerInnerElementPath = this.editorPointerAPI.getInnerElementPath(lastPointer);
      const nextPointerInnerElementPath = [...(lastPointerInnerElementPath || initialPointerInnerElementPath || []), key];
      const nextPointer = this.editorPointerAPI.buildEditorPointer(nextDocumentPointer, nextPointerInnerElementPath);
      return [...acc, nextPointer];
    }, [initialPointer]);
  }
  getChildren(editorPointer, componentManifest, options) {
    if (options === void 0) {
      options = {
        isRecursive: true
      };
    }
    if (!componentManifest || !componentManifest.elements) {
      return [];
    }
    const innerElements = componentManifest.elements;
    return Object.entries(innerElements || {}).flatMap(_ref3 => {
      let [key, element] = _ref3;
      if (element === undefined) {
        return [];
      }
      const documentPointer = this.editorPointerAPI.getDocumentPointer(editorPointer);
      const innerElementPath = this.editorPointerAPI.getInnerElementPath(editorPointer);
      const childPointer = this.editorPointerAPI.buildEditorPointer(documentPointer, [...(innerElementPath || []), key]);
      const innerElementsChildren = getInnerElementsChildren(element);
      if (innerElementsChildren && options.isRecursive) {
        var _element$refElement2;
        return [childPointer, ...this.getChildren(childPointer, element.inlineElement || ((_element$refElement2 = element.refElement) == null ? void 0 : _element$refElement2.resolvedElement), {
          isRecursive: true
        })];
      } else {
        return [childPointer];
      }
    });
  }
  getActionableChildren(editorPointer, manifest, actionName, recursive) {
    if (!(manifest != null && manifest.elements)) {
      return {
        childCount: 0,
        depth: 0,
        actionableElements: []
      };
    }
    let childCount = 0;
    let maxDepth = 0;
    const actionableElements = [];
    for (const [key, element] of Object.entries(manifest.elements)) {
      var _element$refElement3;
      let grandChildCount = 0;
      let grandChildDepth = 0;
      if (!element) {
        continue;
      }
      const childManifest = element.inlineElement || ((_element$refElement3 = element.refElement) == null ? void 0 : _element$refElement3.resolvedElement);
      const isDirectChildActionable = checkElementActionable(childManifest, actionName);
      const childPointer = this.editorPointerAPI.buildEditorPointer(this.editorPointerAPI.getDocumentPointer(editorPointer), [...(this.editorPointerAPI.getInnerElementPath(editorPointer) || ['root']), key]);
      if (recursive) {
        const nestedResult = this.getActionableChildren(childPointer, childManifest, actionName, true);
        grandChildCount = nestedResult.childCount;
        grandChildDepth = nestedResult.depth;
      }
      if (isDirectChildActionable || grandChildCount > 0) {
        childCount++;
        maxDepth = Math.max(maxDepth, grandChildDepth + 1);
        actionableElements.push({
          editorPointer: childPointer,
          displayName: (childManifest == null ? void 0 : childManifest.displayName) || key,
          childCount: grandChildCount
        });
      }
    }
    return {
      childCount,
      depth: maxDepth,
      actionableElements
    };
  }
  getActionableElements(editorPointer, componentManifest, actionName, config) {
    if (config === void 0) {
      config = {
        recursive: false
      };
    }
    const elements = [];
    const isRootActionable = checkElementActionable(componentManifest, actionName);
    const {
      childCount,
      depth,
      actionableElements
    } = this.getActionableChildren(editorPointer, componentManifest, actionName, config.recursive ?? false);

    // Don't Include root even if it has actionable children
    if (isRootActionable) {
      var _editorPointer$innerE;
      elements.push({
        editorPointer,
        displayName: (componentManifest == null ? void 0 : componentManifest.displayName) ?? ((_editorPointer$innerE = editorPointer.innerElementPath) == null ? void 0 : _editorPointer$innerE.at(-1)) ?? 'root',
        childCount
      });
    }
    elements.push(...actionableElements);
    return {
      depth,
      elements
    };
  }
  getChildrenElementManifests(manifest) {
    if (!manifest || !manifest.elements) {
      return {};
    }
    const manifests = {};
    Object.entries(manifest.elements || {}).forEach(_ref4 => {
      var _element$refElement4;
      let [path, element] = _ref4;
      const _manifest = (element == null ? void 0 : element.inlineElement) || (element == null || (_element$refElement4 = element.refElement) == null ? void 0 : _element$refElement4.resolvedElement);
      if (_manifest) {
        manifests[path] = _manifest;
        const children = this.getChildrenElementManifests(_manifest);
        Object.keys(children).forEach(childPath => {
          manifests[`${path}.${childPath}`] = children[childPath];
        });
      }
    });
    return manifests;
  }
}
//# sourceMappingURL=ManifestStructureHelpers.js.map